import { HouseIcon, RefreshCcwIcon } from "lucide-react";
import React, { ReactNode } from "react";
import Button from "./Button";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console for debugging
    console.group("🚨 ErrorBoundary caught an error");
    console.error("Error:", error);
    console.error("Error Info:", errorInfo);
    console.groupEnd();
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div className="flex min-h-screen items-center justify-center bg-white p-6 dark:bg-zinc-900">
            <div className="max-w-md text-center">
              {/* Error Icon */}
              <div className="mx-auto mb-6 h-14 w-14 text-red-500">
                <svg
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  className="h-full w-full"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                  />
                </svg>
              </div>

              {/* Error Title */}
              <h1 className="mb-4 text-3xl font-bold text-gray-900 dark:text-white">
                Something went wrong
              </h1>

              {/* Error Description */}
              <p className="mb-8 text-gray-600 dark:text-gray-400">
                We encountered an unexpected error. Please try refreshing the
                page or go back to the homepage.
              </p>

              {/* Error Details for Development */}
              {import.meta.env.DEV && this.state.error && (
                <div className="mb-8 rounded-xl bg-gray-100 p-4 text-left dark:bg-zinc-800">
                  <details>
                    <summary className="cursor-pointer text-sm font-medium text-gray-700 dark:text-gray-300">
                      Error Details
                    </summary>

                    <div className="font-mono text-xs text-red-600 dark:text-red-400">
                      <div className="mb-2">
                        <strong>Error:</strong> {this.state.error.message}
                      </div>
                      {this.state.error.stack && (
                        <pre className="max-h-32 overflow-auto text-xs whitespace-pre-wrap">
                          {this.state.error.stack}
                        </pre>
                      )}
                    </div>
                  </details>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col justify-center gap-3 sm:flex-row">
                <Button
                  title="Try Again"
                  onClick={() => window.location.reload()}
                  icon={RefreshCcwIcon}
                  className="px-6 py-2.5"
                >
                  Try Again
                </Button>

                <Button
                  title="Go Home"
                  onClick={() => window.location.replace("/")}
                  color="secondary"
                  icon={HouseIcon}
                  className="px-6 py-2.5"
                >
                  Go Home
                </Button>
              </div>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
